import { computed, type MaybeRef, onScopeDispose, ref } from '@/composition/vue/compositionApi'
import { TmComponentCompositionError } from '@/core/error/composition/tmComponentCompositionError'
import { keyCodes } from '@/constants/keyCodes'

export const useFieldSelectPopupFit = (inputRef: MaybeRef<any>) => {
  const width = ref(0)

  const getElement = () => {
    const element = inputRef.value?.$el
    if (!element || !(element instanceof HTMLElement)) {
      throw new TmComponentCompositionError('Element not found')
    }

    return element
  }

  const updateWidth = () => {
    const element = getElement()
    width.value = element.offsetWidth
  }

  const popupContentStyle = computed(() => {
    return `width: ${width.value}px;`
  })

  return {
    popupContentStyle,
    updateWidth,
  }
}

export type FieldSelectTabOwnershipEntry = {
  hasKeyboard: () => boolean
}

const tabOwnershipEntries = new Set<FieldSelectTabOwnershipEntry>()
let entryOfLastTabKeydown: FieldSelectTabOwnershipEntry | null = null

const onDocumentKeydown = (event: KeyboardEvent) => {
  if (event.key !== keyCodes.keyTab) {
    return
  }
  entryOfLastTabKeydown = Array.from(tabOwnershipEntries).findLast((entry) => entry.hasKeyboard()) ?? null
}

const registerTabOwnershipEntry = (entry: FieldSelectTabOwnershipEntry): void => {
  if (tabOwnershipEntries.size === 0) {
    document.addEventListener('keydown', onDocumentKeydown, true)
  }
  tabOwnershipEntries.add(entry)
}

const unregisterTabOwnershipEntry = (entry: FieldSelectTabOwnershipEntry): void => {
  if (entryOfLastTabKeydown === entry) {
    entryOfLastTabKeydown = null
  }
  if (!tabOwnershipEntries.delete(entry) || tabOwnershipEntries.size > 0) {
    return
  }
  document.removeEventListener('keydown', onDocumentKeydown, true)
}

type FieldSelectTabOwnership = {
  isTabPressedInsideField: () => boolean
}

export const useFieldSelectTabOwnership = (hasKeyboard: () => boolean): FieldSelectTabOwnership => {
  const entry: FieldSelectTabOwnershipEntry = { hasKeyboard }

  registerTabOwnershipEntry(entry)
  onScopeDispose(() => unregisterTabOwnershipEntry(entry))

  return {
    isTabPressedInsideField: () => entryOfLastTabKeydown === entry,
  }
}
